// Mapping from framework markdown documents to org-specific generation keys.
//
// The Scalent Brand Framework has 28 fillable documents across naming, legal,
// brand identity, SOPs, reports, and governance. When a user finishes an
// organization interview, the platform generates an adapted version of each
// document for the new organization. The keys here are the canonical IDs used
// in the API, the DB documents_json blob, and the UI tab list.

import type { Locale } from '../i18n';

export type OrgDocumentSection = 'naming' | 'legal' | 'brandIdentity' | 'sops' | 'reports' | 'governance';

export type OrgDocumentKey =
  // Naming
  | 'naming_methodology'
  | 'industry_naming_matrix'
  | 'name_evaluation_scorecard'
  | 'trademark_screening_checklist'
  // Legal
  | 'entity_selection_framework'
  | 'jurisdiction_matrix'
  | 'international_holding_structures'
  | 'compliance_checklist'
  // Brand identity
  | 'brand_positioning_template'
  | 'tone_of_voice_guide'
  | 'visual_identity_brief'
  | 'brand_guidelines_checklist'
  // SOPs
  | 'sop_template'
  | 'sop_index'
  | 'finance_sop'
  | 'hr_sop'
  | 'it_sop'
  | 'legal_sop'
  | 'marketing_sop'
  | 'operations_sop'
  // Reports
  | 'executive_report_template'
  | 'quarterly_business_review_template'
  | 'status_report_template'
  | 'report_structures'
  // Governance
  | 'approval_matrix'
  | 'document_control_policy'
  | 'review_cycle_calendar';

export interface OrgDocumentMeta {
  key: OrgDocumentKey;
  section: OrgDocumentSection;
  titleEn: string;
  titleDe: string;
  /** The framework markdown document used as the structural template. */
  source: string;
  /** Short description shown in the UI tab. */
  summaryEn: string;
  summaryDe: string;
}

// Source = the corresponding framework doc. We import from data at runtime in
// the function file (the build-data.js script emits this content as TypeScript)
// and pass it through to the LLM. The list here is what the UI / API iterates
// over; the actual source strings are resolved when a doc is generated.

export const ORG_DOCUMENTS: OrgDocumentMeta[] = [
  // Naming
  { key: 'naming_methodology', section: 'naming', titleEn: 'Naming methodology', titleDe: 'Namensmethodik',
    source: 'naming.naming_methodology',
    summaryEn: 'Step-by-step process for naming this organization.',
    summaryDe: 'Schritt-für-Schritt-Prozess zur Namensgebung dieser Organisation.' },
  { key: 'industry_naming_matrix', section: 'naming', titleEn: 'Industry naming matrix', titleDe: 'Branchen-Namensmatrix',
    source: 'naming.industry_naming_matrix',
    summaryEn: 'Naming archetypes and suffixes applied to this org’s industry.',
    summaryDe: 'Namens-Archetypen und Suffixe, angewendet auf die Branche dieser Org.' },
  { key: 'name_evaluation_scorecard', section: 'naming', titleEn: 'Name evaluation scorecard', titleDe: 'Bewertungs-Scorecard',
    source: 'naming.name_evaluation_scorecard',
    summaryEn: 'Quantitative scorecard for this org’s candidate names.',
    summaryDe: 'Quantitatives Scorecard für die Namenskandidaten dieser Org.' },
  { key: 'trademark_screening_checklist', section: 'naming', titleEn: 'Trademark screening checklist', titleDe: 'Markenprüfungs-Checkliste',
    source: 'naming.trademark_screening_checklist',
    summaryEn: 'Trademark and domain screening steps for this org.',
    summaryDe: 'Schritte zur Marken- und Domain-Prüfung für diese Org.' },
  // Legal
  { key: 'entity_selection_framework', section: 'legal', titleEn: 'Entity selection framework', titleDe: 'Rechtsform-Wahl',
    source: 'legal.entity_selection_framework',
    summaryEn: 'Recommended legal entity and rationale.',
    summaryDe: 'Empfohlene Rechtsform und Begründung.' },
  { key: 'jurisdiction_matrix', section: 'legal', titleEn: 'Jurisdiction matrix', titleDe: 'Gerichtsbarkeitsmatrix',
    source: 'legal.jurisdiction_matrix',
    summaryEn: 'Jurisdictions compared with this org in mind.',
    summaryDe: 'Gerichtsbarkeiten, verglichen mit dieser Org.' },
  { key: 'international_holding_structures', section: 'legal', titleEn: 'International holding structures', titleDe: 'Internationale Holdingstrukturen',
    source: 'legal.international_holding_structures',
    summaryEn: 'Holding structure options if this org expands internationally.',
    summaryDe: 'Holding-Strukturoptionen, falls diese Org international expandiert.' },
  { key: 'compliance_checklist', section: 'legal', titleEn: 'Legal and regulatory compliance checklist', titleDe: 'Compliance-Checkliste',
    source: 'legal.compliance_checklist',
    summaryEn: 'Compliance obligations for the chosen entity and jurisdiction.',
    summaryDe: 'Compliance-Pflichten für die gewählte Rechtsform und Gerichtsbarkeit.' },
  // Brand
  { key: 'brand_positioning_template', section: 'brandIdentity', titleEn: 'Brand positioning', titleDe: 'Markenpositionierung',
    source: 'brandIdentity.brand_positioning_template',
    summaryEn: 'Positioning statement, audience, and brand pillars.',
    summaryDe: 'Positionierungsaussage, Zielgruppe und Markensäulen.' },
  { key: 'tone_of_voice_guide', section: 'brandIdentity', titleEn: 'Tone of voice guide', titleDe: 'Tone-of-Voice-Leitfaden',
    source: 'brandIdentity.tone_of_voice_guide',
    summaryEn: 'Voice, vocabulary, and tone for this organization.',
    summaryDe: 'Stimme, Vokabular und Tonfall für diese Organisation.' },
  { key: 'visual_identity_brief', section: 'brandIdentity', titleEn: 'Visual identity brief', titleDe: 'Visuelle Identität',
    source: 'brandIdentity.visual_identity_brief',
    summaryEn: 'Logo, palette, typography, and visual direction.',
    summaryDe: 'Logo, Palette, Typografie und visuelle Richtung.' },
  { key: 'brand_guidelines_checklist', section: 'brandIdentity', titleEn: 'Brand guidelines checklist', titleDe: 'Markenleitlinien-Checkliste',
    source: 'brandIdentity.brand_guidelines_checklist',
    summaryEn: 'Guidelines governance for this brand.',
    summaryDe: 'Leitlinien-Governance für diese Marke.' },
  // SOPs
  { key: 'sop_template', section: 'sops', titleEn: 'Standard operating procedure template', titleDe: 'SOP-Vorlage',
    source: 'sops.sop_template',
    summaryEn: 'SOP template filled in for this org’s first procedure.',
    summaryDe: 'SOP-Vorlage, ausgefüllt für die erste Prozedur dieser Org.' },
  { key: 'sop_index', section: 'sops', titleEn: 'SOP index', titleDe: 'SOP-Index',
    source: 'sops.sop_index',
    summaryEn: 'List of SOPs to author in the next 12 months.',
    summaryDe: 'Liste der in den nächsten 12 Monaten zu erstellenden SOPs.' },
  { key: 'finance_sop', section: 'sops', titleEn: 'Finance SOP', titleDe: 'Finanz-SOP',
    source: 'sops.finance',
    summaryEn: 'Finance standard operating procedure.',
    summaryDe: 'Standardarbeitsanweisung Finanzen.' },
  { key: 'hr_sop', section: 'sops', titleEn: 'HR SOP', titleDe: 'HR-SOP',
    source: 'sops.hr',
    summaryEn: 'HR standard operating procedure.',
    summaryDe: 'Standardarbeitsanweisung HR.' },
  { key: 'it_sop', section: 'sops', titleEn: 'IT SOP', titleDe: 'IT-SOP',
    source: 'sops.it',
    summaryEn: 'IT standard operating procedure.',
    summaryDe: 'Standardarbeitsanweisung IT.' },
  { key: 'legal_sop', section: 'sops', titleEn: 'Legal SOP', titleDe: 'Recht-SOP',
    source: 'sops.legal',
    summaryEn: 'Legal standard operating procedure.',
    summaryDe: 'Standardarbeitsanweisung Recht.' },
  { key: 'marketing_sop', section: 'sops', titleEn: 'Marketing SOP', titleDe: 'Marketing-SOP',
    source: 'sops.marketing',
    summaryEn: 'Marketing standard operating procedure.',
    summaryDe: 'Standardarbeitsanweisung Marketing.' },
  { key: 'operations_sop', section: 'sops', titleEn: 'Operations SOP', titleDe: 'Operations-SOP',
    source: 'sops.operations',
    summaryEn: 'Operations standard operating procedure.',
    summaryDe: 'Standardarbeitsanweisung Operations.' },
  // Reports
  { key: 'executive_report_template', section: 'reports', titleEn: 'Executive report template', titleDe: 'Executive-Report-Vorlage',
    source: 'reports.executive_report_template',
    summaryEn: 'Executive report layout for this org.',
    summaryDe: 'Executive-Report-Layout für diese Org.' },
  { key: 'quarterly_business_review_template', section: 'reports', titleEn: 'Quarterly business review', titleDe: 'QBR-Vorlage',
    source: 'reports.quarterly_business_review_template',
    summaryEn: 'QBR template for this org.',
    summaryDe: 'QBR-Vorlage für diese Org.' },
  { key: 'status_report_template', section: 'reports', titleEn: 'Status report template', titleDe: 'Statusbericht-Vorlage',
    source: 'reports.status_report_template',
    summaryEn: 'Status report template for this org.',
    summaryDe: 'Statusbericht-Vorlage für diese Org.' },
  { key: 'report_structures', section: 'reports', titleEn: 'Report structures', titleDe: 'Berichtsstrukturen',
    source: 'reports.report_structures',
    summaryEn: 'Canonical report structures adopted by this org.',
    summaryDe: 'Übernommene Berichtsstrukturen für diese Org.' },
  // Governance
  { key: 'approval_matrix', section: 'governance', titleEn: 'Approval matrix', titleDe: 'Genehmigungsmatrix',
    source: 'governance.approval_matrix',
    summaryEn: 'Who approves what in this organization.',
    summaryDe: 'Wer was in dieser Organisation genehmigt.' },
  { key: 'document_control_policy', section: 'governance', titleEn: 'Document control policy', titleDe: 'Dokumentenkontrollrichtlinie',
    source: 'governance.document_control_policy',
    summaryEn: 'Versioning and review policy for this org’s documents.',
    summaryDe: 'Versions- und Review-Politik für die Dokumente dieser Org.' },
  { key: 'review_cycle_calendar', section: 'governance', titleEn: 'Review cycle calendar', titleDe: 'Review-Zyklus-Kalender',
    source: 'governance.review_cycle_calendar',
    summaryEn: 'Annual review cadence for this org’s documents.',
    summaryDe: 'Jährlicher Review-Rhythmus für die Dokumente dieser Org.' },
];

export const ORG_DOCUMENT_KEYS: OrgDocumentKey[] = ORG_DOCUMENTS.map((d) => d.key);

export function findOrgDocument(key: string): OrgDocumentMeta | undefined {
  return ORG_DOCUMENTS.find((d) => d.key === key);
}

export const ORG_DOCUMENT_SECTION_LABELS_EN: Record<OrgDocumentSection, string> = {
  naming: 'Naming',
  legal: 'Legal',
  brandIdentity: 'Brand identity',
  sops: 'SOPs',
  reports: 'Reports',
  governance: 'Governance',
};

export const ORG_DOCUMENT_SECTION_LABELS_DE: Record<OrgDocumentSection, string> = {
  naming: 'Namensgebung',
  legal: 'Recht',
  brandIdentity: 'Markenidentität',
  sops: 'SOPs',
  reports: 'Berichte',
  governance: 'Governance',
};

export function orgDocumentTitle(meta: OrgDocumentMeta, locale: Locale): string {
  return locale === 'de' ? meta.titleDe : meta.titleEn;
}

export function orgDocumentSummary(meta: OrgDocumentMeta, locale: Locale): string {
  return locale === 'de' ? meta.summaryDe : meta.summaryEn;
}

export function orgDocumentSectionLabel(section: OrgDocumentSection, locale: Locale): string {
  return locale === 'de' ? ORG_DOCUMENT_SECTION_LABELS_DE[section] : ORG_DOCUMENT_SECTION_LABELS_EN[section];
}